import { Component, DestroyRef, inject, signal } from '@angular/core';
import {
  Event as RouterEvent,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
} from '@angular/router';

/**
 * Thin top-of-page progress bar for route navigation, mounted once at the
 * root so every route change (including a lazy chunk still downloading) has
 * visible feedback instead of an instant, unannounced content swap.
 */
@Component({
  selector: 'app-route-progress',
  template: `<div class="route-progress" [class.on]="visible()" [style.width.%]="width()"></div>`,
  styleUrl: './route-progress.scss',
})
export class RouteProgress {
  visible = signal(false);
  width = signal(0);

  // Even a same-tick local navigation gets a floor duration -- the point is
  // consistent, perceptible feedback on every route change, not strict
  // accuracy to how long the navigation actually took.
  private static readonly MIN_VISIBLE_MS = 320;

  private hideTimer?: ReturnType<typeof setTimeout>;
  private growTimer?: ReturnType<typeof setTimeout>;
  private startedAt = 0;

  constructor(router: Router, destroyRef: DestroyRef) {
    const sub = router.events.subscribe((event: RouterEvent) => {
      if (event instanceof NavigationStart) {
        this.start();
      } else if (
        event instanceof NavigationEnd ||
        event instanceof NavigationCancel ||
        event instanceof NavigationError
      ) {
        this.finish();
      }
    });
    destroyRef.onDestroy(() => sub.unsubscribe());
  }

  private start() {
    clearTimeout(this.hideTimer);
    clearTimeout(this.growTimer);
    this.startedAt = Date.now();
    this.visible.set(true);
    this.width.set(30);
    // Creep toward (but never reach) completion while the navigation is in
    // flight, so a slow lazy-chunk download still reads as "in progress".
    this.growTimer = setTimeout(() => this.width.set(65), 220);
  }

  private finish() {
    clearTimeout(this.growTimer);
    const elapsed = Date.now() - this.startedAt;
    const wait = Math.max(0, RouteProgress.MIN_VISIBLE_MS - elapsed);
    this.hideTimer = setTimeout(() => {
      this.width.set(100);
      this.hideTimer = setTimeout(() => {
        this.visible.set(false);
        this.width.set(0);
      }, 180);
    }, wait);
  }
}
